import React, { useEffect } from 'react';

// Types
import { Workspace } from 'Shared/types/Workspace.type';
import { AgendaBuilderSteps } from '../AgendaBuilder';
import { WorkspaceTemplateType } from 'Shared/constants/types';

// Hooks
import { useWorkspaceTemplates } from 'Shared/utils/hooks/use-workspace-templates';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useAgendaBuilderTracking } from 'Shared/tracking/use-agenda-builder-tracking';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpPageLayout, WpPageBody } from 'Wpds/components/page-layout';
import WorkspaceOneOnOneAvatars from '../../../partials/WorkspaceOneOnOneAvatars';
import WorkspaceTemplateBlock from 'Shared/components/TemplateGallery/WorkspaceTemplateBlock/WorkspaceTemplateBlock';

// Styles
import './AgendaBuilderStartScreen.scss';

type Props = {
  workspace: Workspace;
  handleSetState: Function;
};

const AgendaBuilderStartScreen = ({ workspace, handleSetState }: Props) => {
  const { trackViewedWorkspaceAgendaBuilder, trackClickedCreateNewTemplate } = useAgendaBuilderTracking({ workspace });
  const { id: currentUserId } = useCurrentUser();

  const { templates, isApplyingTemplate, handleApplyTemplate, isFetchingTemplates } = useWorkspaceTemplates({
    workspace,
    type: WorkspaceTemplateType.oneOnOne,
    fetchOnMount: true,
  });

  const sortedTemplates = templates.sort((a, b) => {
    // Priority for templates recommended by PI
    if (a.piRecommended !== b.piRecommended) {
      return a.piRecommended ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  const hasBuiltTemplate = templates.find(template => template.userId === currentUserId);

  const handleApplyBlankTemplate = () => {
    handleApplyTemplate({ template: null, sourcePage: 'template_wizard' });
  };

  const handleCreateClick = () => {
    trackClickedCreateNewTemplate({ hasOtherTemplates: Boolean(hasBuiltTemplate) });
    handleSetState(AgendaBuilderSteps.build);
  };

  useEffect(() => {
    trackViewedWorkspaceAgendaBuilder({ showingTemplates: Boolean(hasBuiltTemplate) });
  }, []);

  if (isFetchingTemplates) return null;
  const baseClassName = 'AgendaBuilderStartScreen';
  return (
    <WpPageLayout>
      <WpPageBody className={baseClassName}>
        <WorkspaceOneOnOneAvatars size="xxl" includeeProfilePoover={false} />
        <h3 className={`${baseClassName}__title`}>Welcome to your 1:1 workspace with {workspace.name}</h3>
        <h5 className={`${baseClassName}__description`}>
          You’ll use this space to collaborate on agendas, goals, and share feedback with each other.
        </h5>
        <div className={`${baseClassName}__template-gallery`}>
          <WpContainer onClick={handleCreateClick}>
            <WpContainerBody padding="05" className={`${baseClassName}__template-gallery__new-card`}>
              <div className={`${baseClassName}__template-gallery__new-card__icon`}>
                <WpIcon name="plus-circle" />
              </div>
              Create new
            </WpContainerBody>
          </WpContainer>
          {sortedTemplates.map(template => (
            <WorkspaceTemplateBlock
              key={template.slug}
              template={template}
              isInitialChoice={true}
              isGroupWorkspace={false}
            />
          ))}
        </div>
        <WpButton size="lg" variant="tertiary" loading={isApplyingTemplate} onClick={handleApplyBlankTemplate}>
          Skip, take me to the empty workspace
        </WpButton>
      </WpPageBody>
    </WpPageLayout>
  );
};

export default AgendaBuilderStartScreen;
